import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import {
  checkCodeConfirmation,
  resendCodeConfirmation,
} from "../services/authService";

const EmailVerificationForm = () => {
  const { loading } = useAuth();
  const [d1, setD1] = useState("");
  const [d2, setD2] = useState("");
  const [d3, setD3] = useState("");
  const [d4, setD4] = useState("");
  const [d5, setD5] = useState("");
  const [d6, setD6] = useState("");

  const ref1 = useRef<HTMLInputElement>(null);
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);
  const ref4 = useRef<HTMLInputElement>(null);
  const ref5 = useRef<HTMLInputElement>(null);
  const ref6 = useRef<HTMLInputElement>(null);

  const [disabled, setDisabled] = useState(false);
  const [disabledResend, setDisabledResend] = useState(false);

  const { t } = useTranslation("auth", {
    keyPrefix: "register",
  });

  async function setInput(input: string, value: string) {
    switch (input) {
      case "d1":
        setD1(value);
        if (value !== "") ref2.current?.focus();
        break;
      case "d2":
        setD2(value);
        if (value !== "") ref3.current?.focus();
        break;
      case "d3":
        setD3(value);
        if (value !== "") ref4.current?.focus();
        break;
      case "d4":
        setD4(value);
        if (value !== "") ref5.current?.focus();
        break;
      case "d5":
        setD5(value);
        if (value !== "") ref6.current?.focus();
        break;
      case "d6":
        setD6(value);
        break;
    }
  }

  useEffect(() => {
    if (
      d1 !== "" &&
      d2 !== "" &&
      d3 !== "" &&
      d4 !== "" &&
      d5 !== "" &&
      d6 !== ""
    ) {
      setDisabled(true);
      checkCodeConfirmation({
        code: d1 + d2 + d3 + d4 + d5 + d6,
      }).then((res) => {
        if (res.success) {
          toast({
            title: t("Email verifié"),
          });

          setTimeout(() => {
            localStorage.removeItem("hasSeenTutorial");
            localStorage.removeItem("hasSeenTutorialFunders");
            localStorage.removeItem("hasSeenTutorialFunding");
            localStorage.removeItem("hasSeenTutorialProgram");
            localStorage.removeItem("user");
            localStorage.removeItem("jwtToken");
            localStorage.removeItem("userTheme");
            localStorage.removeItem("refreshToken");
            sessionStorage.removeItem("jwtToken");
            sessionStorage.removeItem("refreshToken");
            window.location.href = "/login?created=1";
          }, 400);
        } else {
          toast({
            title: t("Code invalide"),
          });

          setDisabled(false);
        }
      });
    } else {
      const code = d1 + d2 + d3 + d4 + d5 + d6;

      if (
        code.length === 6 &&
        (d1 === "" ||
          d2 === "" ||
          d3 === "" ||
          d4 === "" ||
          d5 === "" ||
          d6 === "")
      ) {
        setD1(code[0]);
        setD2(code[1]);
        setD3(code[2]);
        setD4(code[3]);
        setD5(code[4]);
        setD6(code[5]);
      }
    }
  }, [d1, d2, d3, d4, d5, d6, t]);

  const resendCode = async () => {
    setDisabledResend(true);

    resendCodeConfirmation().then((res) => {
      if (res.success) {
        toast({
          title: t("Code renvoyé"),
        });
      } else {
        toast({
          title: t("Code non renvoyé"),
        });
      }
      setDisabledResend(false);
    });
  };

  return (
    <Card className="mt-10">
      <CardHeader>
        <CardTitle className="text-center">
          {t("Confirmez votre adresse e-mail")}
        </CardTitle>
        <CardDescription className="px-20 text-center">
          {t(
            "Nous venons de vous envoyer un e-mail avec un code de confirmation. Veuillez saisir ce code dans le champ ci-dessous pour valider votre inscription. Si vous n'avez pas reçu l'e-mail, vérifiez vos spams ou cliquez sur 'Renvoyer le code'."
          )}
        </CardDescription>
      </CardHeader>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <CardContent>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="w-full space-y-8 text-center"
          >
            <div className="flex justify-center space-x-8">
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref1}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d1}
                  onChange={(e) => setInput("d1", e.target.value)}
                />
              </div>
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref2}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d2}
                  onChange={(e) => setInput("d2", e.target.value)}
                />
              </div>
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref3}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d3}
                  onChange={(e) => setInput("d3", e.target.value)}
                />
              </div>
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref4}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d4}
                  onChange={(e) => setInput("d4", e.target.value)}
                />
              </div>
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref5}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d5}
                  onChange={(e) => setInput("d5", e.target.value)}
                />
              </div>
              <div style={{ width: "60px", height: "60px" }}>
                <Input
                  disabled={disabled}
                  ref={ref6}
                  type="text"
                  style={{
                    fontSize: "24px",
                    textAlign: "center",
                  }}
                  className="size-8 p-0 md:size-14"
                  value={d6}
                  onChange={(e) => setInput("d6", e.target.value)}
                />
              </div>
            </div>

            <Button
              disabled={disabledResend}
              type="button"
              variant={"default"}
              className="mx-auto"
              onClick={resendCode}
            >
              {t("Renvoyer le code")}
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
};

export default EmailVerificationForm;
